/**
 * Admin locales API client — CRUD for the language registry.
 *
 * The registry is what makes "add another language" a data operation rather
 * than a code change: a row here plus a dictionary (shipped as JSON, or
 * imported through the lexicon) is the whole of it.
 */

const API_BASE = '/api';

export interface AdminLocale {
  /** BCP-47 tag. The identity used everywhere: URLs, storage, database rows. */
  code: string;
  englishName: string;
  /** Endonym — what a visitor sees in the switcher. */
  nativeName: string;
  /** Next locale to try when a key is missing. Null ends the chain. */
  fallbackCode: string | null;
  isActive: boolean;
  sortOrder: number | null;
  /** How many content_translations rows this locale owns. */
  translationCount: number;
}

async function handle<T>(res: Response): Promise<T> {
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((data as { error?: string })?.error || `Request failed (${res.status})`);
  return data as T;
}

export async function fetchAdminLocales(): Promise<AdminLocale[]> {
  const data = await handle<{ locales: AdminLocale[] }>(
    await fetch(`${API_BASE}/admin-locales.php`, { credentials: 'include' }),
  );
  return data.locales;
}

/** Upsert by code — the same call creates a new locale and edits an existing one. */
export async function saveAdminLocale(locale: Omit<AdminLocale, 'translationCount'>): Promise<AdminLocale> {
  const data = await handle<{ locale: AdminLocale }>(
    await fetch(`${API_BASE}/admin-locales.php`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(locale),
    }),
  );
  return data.locale;
}

/** Removes the locale and every translation it owns. */
export async function deleteAdminLocale(code: string): Promise<{ deleted: number; translations: number }> {
  return handle<{ deleted: number; translations: number }>(
    await fetch(`${API_BASE}/admin-locales.php?code=${encodeURIComponent(code)}`, {
      method: 'DELETE',
      credentials: 'include',
    }),
  );
}
